/*
 * PE variant fallback (theme asset assets/cw-variant-fallback.js).
 * When a shopper picks an option value whose combination with the current selection doesn't exist, select the
 * closest offered variant instead of showing "Unavailable": keep as many other choices as possible, then the nearest
 * width (the "NNmm" in the label), then admin order; in-stock variants first. Crossed-out values are left to the theme.
 * Data: <script data-cw-variant-matrix> inside each variant-picker (snippets/variant-picker.liquid).
 * Runs in the capture phase, before the theme's delegated change handler reads the checked inputs. Keeps the shopper's
 * latest selection per form: whenever a (late, older) response re-renders the picker with anything else, it asks the
 * theme for the latest selection again, so the last click always wins (main product page and quick-buy modal).
 */
(() => {
  if (window.cwVariantFallback) return;
  window.cwVariantFallback = true;

  const intents = new Map(); // form id -> { latest, replays, note, notePosition }
  let replaying = false;

  const mm = (label) => {
    const match = String(label).match(/(?:^|\s)(\d+(?:\.\d+)?)mm\b/i);
    return match ? Number(match[1]) : null;
  };
  const labelText = (input) => (input?.labels?.[0]?.innerText || '').replace(/\s+/g, ' ').trim();
  const optionInputs = (form) =>
    Array.from(form.elements).filter((el) => el instanceof HTMLInputElement && el.dataset.optionPosition);
  const checkedByPosition = (form) => {
    const checked = {};
    optionInputs(form).forEach((el) => { if (el.checked) checked[el.dataset.optionPosition] = el; });
    return checked;
  };
  const selectionKey = (form) => {
    const checked = checkedByPosition(form);
    return Object.keys(checked).sort().map((position) => checked[position].value).join(',');
  };
  const matrixFor = (formId) => {
    const script = document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"] script[data-cw-variant-matrix]`);
    try { return script ? JSON.parse(script.textContent) : null; } catch { return null; }
  };

  function closestVariant(matrix, current, p) {
    const candidates = matrix.variants.filter(([names]) => names[p] === current[p]);
    const pool = candidates.some(([, available]) => available) ? candidates.filter(([, available]) => available) : candidates;
    const scored = pool.map(([names], order) => {
      let kept = 0;
      let distance = 0;
      names.forEach((name, q) => {
        if (q === p) return;
        if (name === current[q]) { kept += 1; return; }
        const from = mm(current[q]);
        const to = mm(name);
        if (from === null) return;
        distance = to === null || distance === null ? null : distance + Math.abs(from - to);
      });
      return { names, kept, distance, order };
    });
    if (scored.some((s) => s.distance === null)) scored.forEach((s) => { s.distance = 0; }); // unparseable: admin order
    scored.sort((a, b) => b.kept - a.kept || a.distance - b.distance || a.order - b.order);
    return scored[0]?.names || null;
  }

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (replaying || !(input instanceof HTMLInputElement) || !input.dataset.optionPosition || !input.checked) return;
    const form = input.form;
    const formId = form?.getAttribute('id');
    if (!formId) return;
    if (input.dataset.productUrl) { intents.delete(formId); return; } // combined-listing value: the theme swaps products
    const entry = { latest: null, replays: 0, note: null, notePosition: null };
    intents.set(formId, entry);

    const matrix = matrixFor(formId);
    const p = Number(input.dataset.optionPosition) - 1;
    const checked = checkedByPosition(form);
    const current = matrix?.complete ? matrix.ids.map((ids, q) => {
      const index = checked[q + 1] ? ids.indexOf(Number(checked[q + 1].value)) : -1;
      return index >= 0 ? matrix.names[q][index] : null;
    }) : [];
    const crossedOut = input.labels?.[0]?.classList.contains('is-disabled');
    const exists = matrix?.variants.some(([names]) => names.every((name, q) => name === current[q]));

    if (matrix?.complete && current.length >= 2 && !current.includes(null) && !crossedOut && !exists) {
      const names = closestVariant(matrix, current, p);
      const inputs = optionInputs(form);
      const targets = (names || []).map((name, q) => {
        const id = matrix.ids[q][matrix.names[q].indexOf(name)];
        return inputs.find((el) => el.dataset.optionPosition === String(q + 1) && Number(el.value) === id);
      });
      if (names && targets.length === current.length && !targets.includes(undefined)) {
        const changes = [];
        targets.forEach((target, q) => {
          if (q === p || target.checked) return;
          changes.push({ position: q + 1, from: labelText(checked[q + 1]), to: labelText(target) });
          target.checked = true;
        });
        if (changes.length) {
          entry.note = `${labelText(input)} doesn't come in ${changes.map((c) => c.from).join(' / ')}, so we've selected ${changes.map((c) => c.to).join(' / ')}.`;
          entry.notePosition = changes[0].position;
        }
      }
    }
    entry.latest = selectionKey(form);
  }, true);

  // Decide after each re-render. product:rerender fires while the form is still attached (in quick-buy the theme then
  // replaces the whole form, so its variant:change never reaches the document); run once the swap has finished.
  document.addEventListener('product:rerender', (event) => {
    const formId = event.target?.getAttribute?.('id');
    if (!formId || event.detail?.productChange || !intents.has(formId)) return;
    setTimeout(() => settle(formId), 0);
  }, true);

  function settle(formId) {
    const entry = intents.get(formId);
    const form = document.getElementById(formId);
    if (!entry || !form) return;

    if (selectionKey(form) !== entry.latest) {
      // An older response re-rendered the picker after a newer click: ask the theme for the latest selection again.
      if (entry.replays >= 5) return;
      const ids = entry.latest.split(',');
      const inputs = optionInputs(form);
      const targets = ids.map((id, q) => inputs.find((el) => el.dataset.optionPosition === String(q + 1) && el.value === id));
      if (targets.includes(undefined)) { intents.delete(formId); return; }
      targets.forEach((target) => { target.checked = true; });
      entry.replays += 1;
      replaying = true;
      try { targets[0].dispatchEvent(new Event('change', { bubbles: true })); } finally { replaying = false; }
      return;
    }

    const variantScript = document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"] script[data-variant]`);
    let variant = null;
    try { variant = JSON.parse(variantScript?.textContent || 'null'); } catch { variant = null; }
    if (!entry.note || !variant) return;
    const anchor = document.querySelector(`input[data-option-position="${entry.notePosition}"][form="${CSS.escape(formId)}"]`);
    const fieldset = anchor?.closest('fieldset');
    if (!fieldset || fieldset.querySelector('.cw-variant-note')) return;
    const element = document.createElement('p');
    element.className = 'cw-variant-note text-sm text-subdued';
    element.setAttribute('role', 'status');
    fieldset.append(element);
    setTimeout(() => { element.textContent = entry.note; }, 100); // the live region must exist before its text changes
  }
})();
